'use client';

import React from 'react';
import Link from 'next/link';

interface NextBridgeCTAProps {
  currentPage: string;
}

const CTA_MAP: Record<string, { href: string; label: string; tagline: string; stage: string }> = {
  'keys-to-kulli-haal': {
    href: '/bridge-environment-design',
    label: 'Bridge 01: Environment Design',
    stage: 'NEXT BRIDGE',
    tagline: 'The curriculum meets the world — step through the veil into creation itself.',
  },
  'bridge-environment-design': {
    href: '/ayat-in-creation',
    label: 'Ayat in Creation',
    stage: 'NEXT STAGE',
    tagline: 'The bridge has been crossed. Now witness the Signs in the horizons.',
  },
  'ayat-in-creation': {
    href: '/bridge-irl-application',
    label: 'Bridge 02: IRL Application',
    stage: 'NEXT BRIDGE',
    tagline: 'Recognition becomes practice — carry the Ayat into your immediate reality.',
  },
  'bridge-irl-application': {
    href: '/inner-reality-lens',
    label: 'Inner Reality Lens',
    stage: 'NEXT STAGE',
    tagline: 'The bridge has been crossed. Now apply the lens to your locality.',
  },
  'inner-reality-lens': {
    href: '/bridge-resource-guide',
    label: 'Bridge 03: Resource Guide',
    stage: 'NEXT BRIDGE',
    tagline: 'Recognition becomes action — the lens reveals the Amanah of caretaking.',
  },
  'bridge-resource-guide': {
    href: '/muslim-resource-guide',
    label: 'Muslim Resource Guide',
    stage: 'NEXT STAGE',
    tagline: 'The bridge has been crossed. Now enter the Caretaker\'s Mata.',
  },
  'muslim-resource-guide': {
    href: '/keys-to-kulli-haal',
    label: 'Return to Keys to Kulli Haal',
    stage: 'RETURN TO ORIGIN',
    tagline: 'The arc completes. Every ending is a new beginning — Alhamdulilah.',
  },
};

export default function NextBridgeCTA({ currentPage }: NextBridgeCTAProps) {
  const cta = CTA_MAP[currentPage];
  if (!cta) return null;

  return (
    <section className="relative overflow-hidden interactive-section" style={{ borderTop: '1px solid rgba(139,105,20,0.2)' }}>
      {/* Geometric star overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.08 }}>
        <svg width="100%" height="100%" viewBox="0 0 800 200" preserveAspectRatio="xMidYMid slice">
          {[100, 400, 700].map((cx) => (
            <g key={cx}>
              {[0, 30, 60, 90, 120, 150].map((angle) => (
                <line
                  key={angle}
                  x1={cx}
                  y1="100"
                  x2={cx + 120 * Math.cos((angle * Math.PI) / 180)}
                  y2={100 + 120 * Math.sin((angle * Math.PI) / 180)}
                  stroke="rgba(139,105,20,0.8)"
                  strokeWidth="0.5"
                />
              ))}
              <circle cx={cx} cy="100" r="60" fill="none" stroke="rgba(139,105,20,0.5)" strokeWidth="0.5" />
              <circle cx={cx} cy="100" r="30" fill="none" stroke="rgba(139,105,20,0.5)" strokeWidth="0.5" />
            </g>
          ))}
        </svg>
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-8 py-16 z-10">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="flex-1">
            <p className="font-mono text-xs tracking-widest uppercase mb-3" style={{ color: 'rgba(139,105,20,0.7)' }}>{cta.stage}</p>
            <h2 className="font-mono text-2xl md:text-3xl font-black uppercase tracking-tight mb-4" style={{ color: '#F5F0E8' }}>
              {cta.label}
            </h2>
            <p className="font-mono text-sm max-w-lg leading-relaxed italic" style={{ color: 'rgba(245,240,232,0.45)' }}>
              &ldquo;{cta.tagline}&rdquo;
            </p>
          </div>
          <div className="flex-shrink-0">
            <Link
              href={cta.href}
              className="group flex items-center gap-4 font-mono text-xs font-black tracking-widest uppercase px-8 py-5 transition-all duration-300"
              style={{ background: 'rgba(139,105,20,0.9)', color: '#F5F0E8', border: '1px solid rgba(179,138,42,0.4)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(179,138,42,0.95)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(139,105,20,0.9)'; }}
            >
              <span>{cta.label}</span>
              <svg width="20" height="20" viewBox="0 0 20 20" className="group-hover:translate-x-1 transition-transform">
                <path d="M4 10h12M12 6l4 4-4 4" stroke="#F5F0E8" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Gold pixel border bottom */}
        <div className="pixel-border-gold-bottom" />
      </div>
    </section>
  );
}
